import { useCallback, useMemo } from 'react';
import type { Observable } from 'rxjs';
import { useMixpanel } from '../../contexts';
import { useObservableValue } from '../../hooks';
import { MixpanelEvent, MixpanelEventProperty } from '../../tokens';
import type { MarketDataSnapshot, Security } from '../../types';
import { format } from '../../utils';
import { QuickOptions, type QuickOptionsProps } from './QuickOptions';
import { calculatePrice } from './helpers/calculatePrice';
import { isMarketDataValid } from './helpers/isMarketDataValid';
import { isQuickOptionDisabled } from './helpers/isQuickOptionDisabled';

export interface PriceQuickOption {
  label: string;
  value: string;
}

export type PriceInputMode = 'Increment' | 'Reset';

export interface PriceQuickOptionsProps<T> extends Omit<QuickOptionsProps<T>, 'onOptionClick' | 'options'> {
  security: Security;
  options: PriceQuickOption[];
  onChange: (value: string) => void;
  marketDataObs: Observable<MarketDataSnapshot | undefined>;
  value?: string;
  priceInputMode?: PriceInputMode;
  narrow?: boolean;
}

export const PriceQuickOptions = <T,>({
  security,
  options,
  onChange,
  marketDataObs,
  disabled,
  value,
  priceInputMode,
  narrow = false,
  ...otherQuickOptionProps
}: PriceQuickOptionsProps<T>) => {
  const marketData = useObservableValue(() => marketDataObs, [marketDataObs]);
  const mixpanel = useMixpanel();

  const handleOptionClicked = useCallback(
    (option: string) => {
      const result = calculatePrice({
        option,
        marketData,
        priceInputMode,
        priceValue: value,
        symbol: security.Symbol,
      });

      if (!result) {
        return;
      }

      if (result.eventType === 'bid') {
        mixpanel.track(MixpanelEvent.PriceOffsetBid);
      } else if (result.eventType === 'offer') {
        mixpanel.track(MixpanelEvent.PriceOffsetOffer);
      } else {
        mixpanel.track(MixpanelEvent.PriceOffsetBPS, { [MixpanelEventProperty.Value]: result.bpsValue });
      }

      onChange(format(result.price, { spec: security.MinPriceIncrement, pretty: false, truncate: true }));
    },
    [marketData, onChange, security.MinPriceIncrement, security.Symbol, priceInputMode, value, mixpanel]
  );

  const optionsWithLabels = useMemo(() => {
    return options.map(option => ({
      label: narrow ? makeNarrow(option.label) : option.label,
      value: option.value,
    }));
  }, [narrow, options]);
  return (
    <QuickOptions
      {...otherQuickOptionProps}
      options={optionsWithLabels}
      onOptionClick={handleOptionClicked}
      disabled={disabled || !isMarketDataValid(security.Symbol, marketData)}
      isOptionDisabled={(optionValue: string) =>
        isQuickOptionDisabled({ optionValue, marketData, priceInputMode, priceValue: value })
      }
    />
  );
};

// Done here instead of having two different sets of options
function makeNarrow(str: string): string {
  if (str.includes('bps')) {
    return str.replace('bps', '');
  }
  if (str.includes('Bid')) {
    return str.replace('Bid', 'B');
  }
  if (str.includes('Offer')) {
    return str.replace('Offer', 'O');
  }
  return str;
}
